import { injectable } from 'inversify';
import 'reflect-metadata';
import axiosClient from './axios-client';
import { ERRORS } from '../constants/errors/error-messages.constants';
import { ApiPaginatedResponse, ApiResponse } from '../utils/api-response.utils';
import { TradewindsBaseData } from '../models/tradewinds/tradewinds.model';
import { TradewindsFile } from '../models/tradewinds.model';
import {
  ShopeRankingPeriods,
  ShopsRankingData,
} from '../_api/repositories/ShopsRankingRepository';

const idsArrayToParam = (ids: number[]) => {
  return ids
    .map((id) => {
      return `${id}`;
    })
    .join(',');
};
import { KeyInsight } from '../_api/models/base-module.model';
import { ShopeeForecastData } from '../_api/models/module-b.model';
import { SellerPanelData } from '../_api/models/module-c.model';
import { MetricsMap } from '../utils/tradewinds.util';
import { TradewindsSubscriptionDetails } from '../_api/repositories/SubscriptionRepository';

@injectable()
export class TradewindsService {
  async getTradewindsSubscriptionDetails(): Promise<
    ApiResponse<TradewindsSubscriptionDetails>
  > {
    try {
      const response = await axiosClient.get<
        ApiResponse<TradewindsSubscriptionDetails>
      >(`/api/tradewinds/subscription-details`);
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getTradewindsBaseModule(
    metric: string | string[],
    period: string
  ): Promise<ApiResponse<TradewindsBaseData[]>> {
    try {
      const response = await axiosClient.get<ApiResponse<TradewindsBaseData[]>>(
        `/api/tradewinds/base-module?metrics=${metric}&periodType=${period}`
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getTradewindsFiles(): Promise<ApiResponse<TradewindsFile[]>> {
    try {
      const response = await axiosClient.get<ApiResponse<TradewindsFile[]>>(
        `/api/tradewinds/file-details`
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async downloadTradewindsBaseModule(): Promise<ApiResponse<string>> {
    try {
      const response = await axiosClient.get<ApiResponse<string>>(
        `/api/tradewinds/base-module/presigned-url`
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getTradewindsBaseModuleKeyInsights(): Promise<
    ApiResponse<KeyInsight[]>
  > {
    try {
      const response = await axiosClient.get<ApiResponse<KeyInsight[]>>(
        `/api/tradewinds/base-module/key-insights`
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getTradewindsModuleA(
    paginated: boolean,
    offset: number,
    periodId: number,
    categoryIds: number[],
    channelIds: number[],
    countryIds: number[],
    limit: number
  ): Promise<ApiPaginatedResponse<ShopsRankingData>> {
    try {
      const response = await axiosClient.get<
        ApiPaginatedResponse<ShopsRankingData>
      >(
        `/api/tradewinds/module-a?paginated=${paginated}&offset=${offset}&periodId=${periodId}&categoryIds=${idsArrayToParam(categoryIds)}&channelIds=${idsArrayToParam(channelIds)}&countryIds=${idsArrayToParam(countryIds)}&limit=${limit}`
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async downloadTradewindsModuleA(): Promise<ApiResponse<string>> {
    try {
      const response = await axiosClient.get<ApiResponse<string>>(
        `/api/tradewinds/module-a/presigned-url`
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getAvailablePeriodModuleA(): Promise<
    ApiResponse<ShopeRankingPeriods[]>
  > {
    try {
      const response = await axiosClient.get<
        ApiResponse<ShopeRankingPeriods[]>
      >(`/api/tradewinds/module-a/periods`);
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getTradewindsModuleB(): Promise<ApiResponse<ShopeeForecastData>> {
    try {
      const response = await axiosClient.get<ApiResponse<ShopeeForecastData>>(
        `/api/tradewinds/module-b`
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getTradewindsModuleBKeyInsights(): Promise<ApiResponse<KeyInsight[]>> {
    try {
      const response = await axiosClient.get<ApiResponse<KeyInsight[]>>(
        `/api/tradewinds/module-b/key-insights`
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async downloadTradewindsModuleB(): Promise<ApiResponse<string>> {
    try {
      const response = await axiosClient.get<ApiResponse<string>>(
        `/api/tradewinds/module-b/presigned-url`
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  // Module C

  async getTradewindsModuleC(
    tab: string,
    channelIds?: number[],
    countryIds?: number[],
    categoryIds?: number[],
    businessSizes?: string[],
    periodName?: string
  ): Promise<ApiResponse<SellerPanelData>> {
    try {
      const metricsName = `metricsName=${MetricsMap[tab as keyof typeof MetricsMap].join(',')}`;
      const channels = `${channelIds && channelIds.length > 0 ? '&channelIds=' + channelIds?.join(',') : ''}`;
      const country = `${countryIds && countryIds.length > 0 ? '&countryIds=' + countryIds?.join(',') : ''}`;
      const category = `${categoryIds && categoryIds.length > 0 ? '&categoryIds=' + categoryIds?.join(',') : ''}`;
      const businessSizesArray = `${businessSizes && businessSizes.length > 0 ? '&businessSizes=' + businessSizes.join(',') : ''}`;
      const period = `${periodName ? '&periodName=' + periodName : ''}`;
      const response = await axiosClient.get<ApiResponse<SellerPanelData>>(
        `/api/tradewinds/module-c?${metricsName}${channels}${country}${category}${businessSizesArray}${period}`.trimEnd()
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async getModuleCCountryFilters(): Promise<
    ApiResponse<{
      countryChannelMap: Record<string, string[]>;
      countryCategoryMap: Record<string, string[]>;
    }>
  > {
    try {
      const response = await axiosClient.get<
        ApiResponse<{
          countryChannelMap: Record<string, string[]>;
          countryCategoryMap: Record<string, string[]>;
        }>
      >('/api/tradewinds/module-c/filters');
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }

  async downloadTradewindsModuleC(): Promise<ApiResponse<string>> {
    try {
      const response = await axiosClient.get<ApiResponse<string>>(
        `/api/tradewinds/module-c/presigned-url`
      );
      return response.data;
    } catch (e: any) {
      if (e.response?.status === 403)
        throw new Error(ERRORS.missingSubscription);
      throw new Error(ERRORS.unexpectedError);
    }
  }
}
